import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** Material-UI */
import { makeStyles, Divider, Drawer, IconButton, Tooltip } from "@material-ui/core";

/** Icons */
import { Icon } from "@iconify/react";

/** Redux */
import { useSelector, useDispatch } from "react-redux";
import { getLeftDrawerOpen, setLeftDrawerOpen } from "../../_store/APP";
import { getSelectedTreeItem } from "../../_store/TREE";

/** UI */
// eslint-disable-next-line
const UnitsSearch = React.lazy(() => import("./UnitsSearch"));
const UnitsTree = React.lazy(() => import("./UnitsTree"));
const Search = React.lazy(() => import("../Search/Search"));

const LeftPanel = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    /** Redux selectors */
    const leftDrawerOpen = useSelector(getLeftDrawerOpen);
    const selectedItem = useSelector(getSelectedTreeItem);
    /** /end Redux selectors */

    /** States */
    const [drawerWidth, setDrawerWidth] = useState(false);
    const [open, setOpen] = useState(leftDrawerOpen);
    const [disabledClose, setDisabledClose] = useState(false);
    const [territorialUnits, setTerritorialUnits] = useState([]);
    const [searchProps, setSearchProps] = useState({
        selectedSearchItem: null,
        expanded: null,
    });
    /** /end States */

    /** Effects */
    useEffect(() => {
        drawerWidth || setDrawerWidth(600);
    }, [drawerWidth]);
    const drawerRef = useRef(null);

    useEffect(() => {
        const resizing = () => setDrawerWidth(window.innerWidth > 600 ? 600 : window.innerWidth);
        window.addEventListener("resize", resizing);
        resizing();
        return () => window.removeEventListener("resize", resizing);
    });

    useEffect(() => {
        setOpen(leftDrawerOpen);
        return () => {
            setSearchProps({ selectedSearchItem: null, expanded: null });
        };
    }, [leftDrawerOpen]);

    useEffect(() => setDisabledClose(!selectedItem), [selectedItem]);
    /** /end Effects */

    /** Styles */
    const useStyles = makeStyles((theme) => ({
        drawerBackdrop: {
            position: "fixed",
            width: "100%", // "36%",
            height: "100%",
            zIndex: 999,
            background: theme.palette.background.main,
            // background:'rgba(0,0,0,0.25)',
            [theme.breakpoints.down("xs")]: {
                width: "100%",
                marginTop: -10,
            },
        },
        navbar: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginRight: -50,
            marginTop: 85,
            [theme.breakpoints.down("xs")]: {
                marginRight: 10,
                marginTop: 20,
            },
        },
        navbarClosed: {
            width: "calc(100% + 50px)",
        },
        btnNav: {
            minWidth: 42,
            minHeight: 42,
            padding: 0,
            color: "#fff",
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
                backgroundColor: theme.palette.primary.main,
            },
        },

        drawerPaper: {
            marginTop: 65,
            backgroundColor: "#F8F9FA", //"transparent",
            width: drawerWidth,
            height: "calc(100% + 65px)",
            maxWidth: "100vw",
            overflowY: "visible",
            visibility: "visible!important",
            transform: `translateX(0px)`,
            border: "none",
            position: "fixed",
            [theme.breakpoints.down("xs")]: {
                marginTop: 60,
            },
        },
        drawerPaperClose: {
            transform: `translateX(-${drawerWidth - 8}px)!important`,
            [theme.breakpoints.down("md")]: {
                position: "absolute",
            },
        },
        wrapper: {
            padding: theme.spacing(0, 0, 0.5, 2),
            marginTop: -130,
            [theme.breakpoints.down("xs")]: {
                marginTop: 16,
                padding: 0,
            },
        },
        divider: {
            width: "calc(100% - 16px)",
            height: "auto",
        },
        infoWrapper: {
            background: "transparent",
            marginLeft: 500,
            width: "40%",
            border: "none",
        },
        infoHeader: {
            display: "flex",
            ...theme.shape.boxedWrapper,
            backgroundColor: "#E5E5E5",
            marginTop: theme.spacing(3),
            color: "#132F4D",
            alignItems: "center",
            justifyContent: "center",
            padding: 40,
        },
        listIconItem: {
            alignSelf: "start",
            justifySelf: "end",
            color: "#000",
            minWidth: "auto",
        },
    }));
    const classes = useStyles();
    /** /end Styles */

    /** Handlers */
    const toggleDrawer = (open) => (event) => {
        if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
            return;
        }
        dispatch(setLeftDrawerOpen(open));
    };

    const handleOnGetTerritorialUnits = ({ territorialUnits }) => setTerritorialUnits(territorialUnits);

    const handleOnSelectUnit = (props) => setSearchProps(props);
    /** /end Handlers */

    return (
        <div>
            <React.Fragment>
                <Drawer
                    ref={drawerRef}
                    anchor={"left"}
                    open={open}
                    variant={"permanent"}
                    classes={{
                        root: classes.drawerRoot,
                        paper: clsx(classes.drawerPaper, open || classes.drawerPaperClose),
                    }}
                >
                    <div className={clsx(classes.navbar, open || classes.navbarClosed)}>
                        <Tooltip
                            title={`${!open ? t("Pokaż") : t("Zwiń")} ${t("drzewko wyboru jednostek terytorialnych")}`}
                            arrow
                            placement="right"
                        >
                            <span>
                                <IconButton
                                    size={"small"}
                                    onClick={toggleDrawer(!open)}
                                    className={classes.btnNav}
                                    disabled={disabledClose}
                                    aria-label={"left-panel"}
                                >
                                    {!!open ? (
                                        <Icon icon="mdi:chevron-left" width="35" height="35" />
                                    ) : (
                                        <Icon icon="mdi:chevron-right" width="35" height="35" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div className={classes.wrapper}>
                        {/* {!!territorialUnits.length && <UnitsSearch territorialUnits={territorialUnits} />} */}
                        {!!territorialUnits.length && <Search onSelectUnit={(props) => handleOnSelectUnit(props)} />}
                        <Divider className={classes.divider} />

                        {!!open && (
                            <UnitsTree
                                onGetTerritorialUnits={(territorialUnits) =>
                                    handleOnGetTerritorialUnits({ territorialUnits })
                                }
                                searchProps={searchProps}
                            />
                        )}
                    </div>
                </Drawer>
                {!!open && <div className={classes.drawerBackdrop} />}
            </React.Fragment>
        </div>
    );
};

export default LeftPanel;
